import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  FileText,
  HelpCircle,
  File,
  Download,
  Eye,
  EyeOff,
  Loader2,
  Upload
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useResources, useCreateResource, useUpdateResource, useDeleteResource } from '../lib/hooks';
import { resourcesApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const ResourcesAdminPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState('guide');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'guide',
    category: '',
    content: '',
    status: 'draft'
  });
  const [faqContent, setFaqContent] = useState({ question: '', answer: '' });

  const { data: resources = [], isLoading } = useResources();
  const createResource = useCreateResource();
  const updateResource = useUpdateResource();
  const deleteResource = useDeleteResource();

  const filteredResources = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = r.type === activeTab;
    return matchesSearch && matchesType;
  });

  const handleOpenDialog = (resource = null) => {
    if (resource) {
      setSelectedResource(resource);
      setFormData({
        title: resource.title,
        type: resource.type,
        category: resource.category,
        content: resource.content || '',
        status: resource.status
      });
      if (resource.type === 'faq') {
        try {
          const parsed = JSON.parse(resource.content);
          setFaqContent({ question: parsed.question || resource.title, answer: parsed.answer || '' });
        } catch (e) {
          setFaqContent({ question: resource.title, answer: resource.content || '' });
        }
      }
    } else {
      setSelectedResource(null);
      setFormData({
        title: '',
        type: activeTab,
        category: '',
        content: '',
        status: 'draft'
      });
      setFaqContent({ question: '', answer: '' });
    }
    setFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedResource) {
        // Update existing resource
        let content = formData.content;
        if (formData.type === 'faq') {
          content = JSON.stringify({ question: faqContent.question, answer: faqContent.answer });
        }
        
        await updateResource.mutateAsync({
          resourceId: selectedResource.id,
          data: {
            title: formData.type === 'faq' ? faqContent.question : formData.title,
            category: formData.category,
            content: content,
            status: formData.status
          }
        });
        toast.success('Ressource mise à jour');
      } else {
        // Create new resource
        const data = new FormData();
        data.append('title', formData.type === 'faq' ? faqContent.question : formData.title);
        data.append('type', formData.type);
        data.append('category', formData.category);
        data.append('status', formData.status);
        
        if (formData.type === 'faq') {
          data.append('content', JSON.stringify({ question: faqContent.question, answer: faqContent.answer }));
        } else {
          data.append('content', formData.content);
        }
        
        if (file && (formData.type === 'guide' || formData.type === 'template')) {
          data.append('file', file);
        }
        
        await createResource.mutateAsync(data);
        toast.success('Ressource créée');
      }
      
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async () => {
    if (!selectedResource) return;
    
    try {
      await deleteResource.mutateAsync(selectedResource.id);
      toast.success('Ressource supprimée');
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (resource) => {
    const newStatus = resource.status === 'published' ? 'draft' : 'published';
    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: { status: newStatus }
      });
      toast.success(`Ressource ${newStatus === 'published' ? 'publiée' : 'mise en brouillon'}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const statusColors = {
    draft: 'bg-muted text-muted-foreground',
    published: 'bg-success/10 text-success'
  };

  const statusLabels = {
    draft: 'Brouillon',
    published: 'Publié'
  };

  const typeIcons = {
    guide: FileText,
    template: File,
    faq: HelpCircle
  };

  const typeLabels = {
    guide: 'Guides',
    template: 'Modèles',
    faq: 'FAQ'
  };

  const categoryOptions = {
    guide: ['Examens', 'Vocabulaire', 'Grammaire', 'Culture', 'Études', 'Vie en Allemagne', 'Autre'],
    template: ['CV', 'Lettre de motivation', 'Documents administratifs', 'Formulaires', 'Autre'],
    faq: ['Inscription', 'Visa', 'Hébergement', 'Études', 'Langue', 'Général']
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resources-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Gestion des ressources</h1>
          <p className="text-muted-foreground mt-1">Créez et gérez les ressources pour les étudiants</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="btn-active" data-testid="add-resource-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une ressource
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="guide" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Guides
          </TabsTrigger>
          <TabsTrigger value="template" className="flex items-center gap-2">
            <File className="w-4 h-4" />
            Modèles
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          {/* Search */}
          <Card className="border-0 shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t("action_search", lang)}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="resource-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {filteredResources.length === 0 ? (
                <div className="text-center py-12">
                  {React.createElement(typeIcons[activeTab], { className: "w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" })}
                  <h3 className="font-semibold text-lg mb-1">{t('resources_no_resources', lang)}</h3>
                  <p className="text-muted-foreground">
                    Ajoutez votre première ressource de type "{typeLabels[activeTab]}"
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('form_title', lang)}</TableHead>
                      <TableHead>{lang === 'de' ? 'Kategorie' : 'Catégorie'}</TableHead>
                      <TableHead>{t('students_status', lang)}</TableHead>
                      {activeTab !== 'faq' && <TableHead>Fichier</TableHead>}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResources.map((resource) => (
                      <TableRow key={resource.id} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {React.createElement(typeIcons[resource.type], { 
                              className: "w-5 h-5 text-muted-foreground" 
                            })}
                            <span className="font-medium">{resource.title}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{resource.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={statusColors[resource.status]}>
                            {statusLabels[resource.status]}
                          </Badge>
                        </TableCell>
                        {activeTab !== 'faq' && (
                          <TableCell>
                            {resource.file_name ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(resourcesApi.getFileUrl(resource.id), '_blank')}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Télécharger
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm">Aucun fichier</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenDialog(resource)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleStatus(resource)}>
                                {resource.status === 'published' ? (
                                  <>
                                    <EyeOff className="w-4 h-4 mr-2" />
                                    Mettre en brouillon
                                  </>
                                ) : (
                                  <>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Publier
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedResource(resource);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedResource ? 'Modifier la ressource' : 'Nouvelle ressource'}
            </DialogTitle>
            <DialogDescription>
              {selectedResource ? 'Modifiez les informations' : `Créer un${formData.type === 'faq' ? 'e FAQ' : formData.type === 'guide' ? ' guide' : ' modèle'}`}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!selectedResource && (
              <div className="space-y-2">
                <Label>Type de ressource</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v, category: '' })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guide">Guide (PDF)</SelectItem>
                    <SelectItem value="template">Modèle de document</SelectItem>
                    <SelectItem value="faq">Question fréquente (FAQ)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.type === 'faq' ? (
              <>
                <div className="space-y-2">
                  <Label>Question *</Label>
                  <Input
                    value={faqContent.question}
                    onChange={(e) => setFaqContent({ ...faqContent, question: e.target.value })}
                    placeholder="Ex: Comment obtenir un visa étudiant ?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Réponse *</Label>
                  <Textarea
                    value={faqContent.answer}
                    onChange={(e) => setFaqContent({ ...faqContent, answer: e.target.value })}
                    placeholder="La réponse à la question..."
                    rows={4}
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={formData.type === 'guide' ? 'Ex: Guide de préparation au TestDaF' : 'Ex: Modèle de CV'}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('form_description', lang)}</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Description de la ressource..."
                    rows={3}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {(categoryOptions[formData.type] || []).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!selectedResource && (formData.type === 'guide' || formData.type === 'template') && (
              <div className="space-y-2">
                <Label>Fichier (PDF ou DOCX)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".pdf,.docx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="flex-1"
                  />
                </div>
                {file && (
                  <p className="text-sm text-muted-foreground">
                    Fichier sélectionné: {file.name}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('students_status', lang)}</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{lang === 'de' ? 'Entwurf' : 'Brouillon'}</SelectItem>
                  <SelectItem value="published">{lang === 'de' ? 'Veröffentlicht' : 'Publié'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={createResource.isPending || updateResource.isPending}>
                {(createResource.isPending || updateResource.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {selectedResource ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer "{selectedResource?.title}" ? 
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleteResource.isPending}
            >
              {deleteResource.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourcesAdminPage;
